import * as React from "react";
import { FormControl, Grid, InputLabel, OutlinedInput } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { Disposition, useCreateDisposition, useUpdateDisposition } from "@/hooks/api/use-campaigns";
import { CustomDrawer } from "@/components/custom-drawer";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { DynamicField } from "@/components/dynamicField/dynamic-field";
import { DynamicFieldType } from "@/components/dynamicField/types";

export function CreateDisposition({
	campaignId,
	disposition,
	isCreateDrawerOpen,
	onCreateDrawerClose,
}: {
	campaignId: string;
	disposition: Disposition | null;
	isCreateDrawerOpen: boolean;
	onCreateDrawerClose: () => void;
}): React.JSX.Element {
	const createDispositionMutation = useCreateDisposition();
	const updateDispositionMutation = useUpdateDisposition();

	const isEdit = !!disposition?.id;

	const methods = useForm<any>({
		defaultValues: {
			name: disposition?.name || "",
			label: disposition?.label || "",
			description: disposition?.description || "",
			usedForAnalyses: disposition?.usedForAnalyses || false,
			sendEmail: disposition?.sendEmail || false,
			countsAsConversion: disposition?.countsAsConversion || false,
		},
	});

	const {
		handleSubmit,
		reset,
		control,
		setError,
		formState: { isSubmitting },
	} = methods;

	React.useLayoutEffect(() => {
		reset({
			name: disposition?.name || "",
			label: disposition?.label || "",
			description: disposition?.description || "",
			usedForAnalyses: disposition?.usedForAnalyses || false,
			sendEmail: disposition?.sendEmail || false,
			countsAsConversion: disposition?.countsAsConversion || false,
		});
	}, [disposition, reset, isCreateDrawerOpen]);

	const onSubmitForm = async (data: any) => {
		try {
			await (isEdit && disposition?.id
				? updateDispositionMutation.mutateAsync({ campaignId: campaignId || "", id: disposition.id, data })
				: createDispositionMutation.mutateAsync({ campaignId: campaignId || "", data }));

			onCreateDrawerClose();
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;
			if (!Array.isArray(errorMessages)) {
				return;
			}

			const errorMessage = errorMessages[0];
			setError(errorMessage.split(" ")[0], { message: errorMessage });
		}
	};

	return (
		<React.Fragment>
			<CustomDrawer
				open={isCreateDrawerOpen}
				onClose={onCreateDrawerClose}
				title={isEdit ? "Edit Disposition" : "Create Disposition"}
				onSubmit={handleSubmit(onSubmitForm)}
				isSubmitButtonDisabled={
					isSubmitting || createDispositionMutation.isPending || updateDispositionMutation.isPending
				}
				submitButtonText={isEdit ? "Update Disposition" : "Create Disposition"}
				width="420px"
			>
				<FormProvider {...methods}>
					<Grid container spacing={2}>
						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							{" "}
							<Controller
								control={control}
								name="name"
								rules={{
									required: "Name is required",
									minLength: {
										value: 2,
										message: "Name must be at least 2 characters",
									},
								}}
								render={({ field, fieldState }) => (
									<FormControl error={Boolean(fieldState.error)} fullWidth required>
										<InputLabel>Name</InputLabel>
										<OutlinedInput {...field} placeholder="Enter name" />
										{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
									</FormControl>
								)}
							/>
						</Grid>

						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							{" "}
							<Controller
								control={control}
								name="label"
								rules={{
									required: "Label is required",
									minLength: {
										value: 2,
										message: "Label must be at least 2 characters",
									},
								}}
								render={({ field, fieldState }) => (
									<FormControl error={Boolean(fieldState.error)} fullWidth required>
										<InputLabel>Label</InputLabel>
										<OutlinedInput {...field} placeholder="Enter label" />
										{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
									</FormControl>
								)}
							/>
						</Grid>

						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							{" "}
							<DynamicField
								control={control}
								type={DynamicFieldType.TEXT_AREA}
								name="description"
								label="Description"
								dynamicField={{
									name: "description",
									type: DynamicFieldType.TEXT_AREA,
									label: "Description",
									placeholder: "Enter description",
									multiline: true,
								}}
							/>
						</Grid>

						{disposition?.isSystem ? null : (
							<Grid
								size={{
									md: 6,
									xs: 6,
								}}
							>
								<DynamicField
									control={control}
									type={DynamicFieldType.BOOLEAN}
									name="usedForAnalyses"
									label="Used For Analysis"
									dynamicField={{
										name: "usedForAnalyses",
										type: DynamicFieldType.BOOLEAN,
										label: "Used For Analysis",
									}}
								/>
							</Grid>
						)}

						<Grid
							size={{
								md: 6,
								xs: 6,
							}}
						>
							<DynamicField
								control={control}
								type={DynamicFieldType.BOOLEAN}
								name="sendEmail"
								label="Send Email"
								dynamicField={{
									name: "sendEmail",
									type: DynamicFieldType.BOOLEAN,
									label: "Send Email",
								}}
							/>
						</Grid>

						{disposition?.isSystem ? null : (
							<Grid
								size={{
									md: 6,
									xs: 6,
								}}
							>
								<DynamicField
									control={control}
									type={DynamicFieldType.BOOLEAN}
									name="countsAsConversion"
									label="Counts As Conversion"
									dynamicField={{
										name: "countsAsConversion",
										type: DynamicFieldType.BOOLEAN,
										label: "Counts As Conversion",
									}}
								/>
							</Grid>
						)}
					</Grid>
				</FormProvider>
			</CustomDrawer>
		</React.Fragment>
	);
}
